import {
  ADMIN_EDIT_MANUAL_ORDER,
  AdminHeader,
  AdminSpinner,
  ArrowRed,
  BrandType,
  CallRecordingAndTranscription,
  CheckRedIcon,
  ChevronSmall,
  CollapsibleComponent,
  CustomerInfo,
  DeleteIconRed,
  Disclaimer,
  Div,
  EditPenIconRed,
  ErrorToast,
  GrayCircleEarphoneIcon,
  H3,
  H5,
  Icon,
  Img,
  ManualIconTooltip,
  OrderCourierIcon,
  OrderCustomerIcon,
  OrderDetailsIcon,
  OrderInfoIcon,
  OrderNotesIcon,
  OrderUtilsIcon,
  P,
  PhoneNumberText,
  PosFailedIcon,
  PosIcon,
  PosSuccessIcon,
  PrinterIcon,
  Provider,
  ProviderConnectionTypeComponent,
  RecoveryIcon,
  ScheduledIconTooltip,
  SuccessToast,
  TextWithTooltipIcon,
  TooltipWrapper,
  UnderlineHoverLink,
  VDriveGetQuoteHistoryResponse,
  VDriveGetRecordingResponse,
  VerticalSeparator,
  centsToDollars,
  formatter,
  getCancelVDriveOrderFee,
  getOrderById,
  getOrdersAssignHistory,
  getOrdersInterceptorCall,
  getVDriveOffers,
  isAdmin,
  isBrandOwner,
  isPartner,
  lightTheme,
  markOrderAsCompleted,
  renderPosImage,
  useOrders,
  reportError,
} from '@vgrubs/components';
import {
  providerLogo,
  providerName,
} from 'components/src/helpers/location-helpers';
import {
  DeliveryType,
  Order,
  OrderStatus,
  PosInjectionStatus,
  VDriveOfferEntity,
} from 'components/src/types/order.types';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import useDeliveriesDetailsSocket from '../Deliveries/components/DeliveriesDetailsSocket';
import { DistancePill } from '../Deliveries/components/DistancePill';
import { HistoryAssign } from '../Deliveries/components/HistoryAssign';
import { CancelVDriveModal } from './CancelVDriveModal';
import { ChangeAddress } from './ChangeAddress';
import { OrdersDropdown } from './components/OrdersDropdown';
import { OrdersStatusPill } from './components/OrdersStatusPill';
import { CourierModal } from './CourierModal';
import { DeleteOrderModal } from './DeleteOrderModal';
import { MarkOrderAsCompletedModal } from './MarkOrderAsCompleted';
import { PoweredByComponent } from './PoweredByComponent';
import { PrintReceiptModal } from './PrintReceiptModal';
import { RecoveryModal } from './RecoveryModal';
import { RecoveryStatusBadge } from './RecoveryStatusBadge';
import { VDriveAssignDriver } from './VDriveAssignDriver';
import { VDriveStatusComponent } from './VDriveStatusComponent';
import { WhatIsThisModal } from './WhatIsThisModal';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';

export const recoveryStatus = {
  RECOVERED: 'recovered',
  ADJUSTMENT: 'adjustment',
  PENDING: 'pending',
  NOT_ELIGIBLE: 'notEligible',
  EVIDENCE_NEEDED: 'evidenceNeeded',
};

export const OrderDetails = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [courierModalIsOpen, setCourierModalIsOpen] = useState(false);
  const [assignDriverModal, setAssignDriverModal] = useState<
    VDriveOfferEntity[] | null
  >(null);
  const [noDriverModal, setNoDriverModal] = useState(false);
  const [whatIsThisModal, setWhatIsThisModal] = useState(false);
  const [changeAddressModal, setChangeAddressModal] = useState<number | null>(
    null
  );
  const [cancelVDriveModal, setCancelVDriveModal] = useState(false);
  const [cancelVDriveOrderFee, setCancelVDriveOrderFee] = useState<number>(0);
  const [isDriverAssigned, setIsDriverAssigned] = useState<boolean>(false);
  const [markOrderAsCompletedModal, setMarkOrderAsCompletedModal] =
    useState(false);

  const { setDeleteManualOrderModal, setOrderToEdit, setPrintOrderModal } =
    useOrders((state) => state);

  const { id } = useParams() as { id: string };

  const [interceptorCallData, setInterceptorCallData] =
    useState<VDriveGetRecordingResponse | null>(null);

  const [interceptorCallCollapse, setInterceptorCallCollapse] = useState(false);
  const refInterceptorCall = useRef<HTMLDivElement>(null);
  const [elementHeightInterceptorCall, setElementHeightInterceptorCall] =
    useState(0);

  useEffect(() => {
    if (refInterceptorCall.current && interceptorCallData?.recordingUrl) {
      setElementHeightInterceptorCall(refInterceptorCall.current.scrollHeight);
    }
  }, [interceptorCallData, refInterceptorCall.current]);

  // The backend answers 400 ("Interceptor call ID not found.") whenever no
  // recording exists, so we only ask for one once the order tells us there is
  // one. interceptorCallId on its own is not a reliable condition.
  const hasCallRecording = data?.vDrive?.hasCallRecording === true;

  async function getInterceptorCallFunc() {
    if (!id) return;
    if (!isAdmin) return;
    try {
      const { data: interceptorCallData } = await getOrdersInterceptorCall(id);
      setInterceptorCallData(interceptorCallData.data);
    } catch (error) {
      console.error('Error getting interceptor call', error);
    }
  }

  useEffect(() => {
    if (!isAdmin || !id || !hasCallRecording) {
      setInterceptorCallData(null);
      return;
    }
    getInterceptorCallFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, hasCallRecording]);

  useDeliveriesDetailsSocket({
    deliveryStatus: data?.vDrive?.fdStatus,
    orderId: data?.id?.toString() || '',
    getData: getOrderDetails,
  });

  async function getOrderDetails(noLoader?: boolean) {
    if (!id) return;
    try {
      if (!noLoader) {
        setLoading(true);
      }
      const { data } = await getOrderById(id);
      setData(data.data);
    } catch (error) {
      ErrorToast('No order found with this ID.');
      setLoading(false);
    } finally {
      if (!noLoader) {
        setLoading(false);
      }
    }
  }

  async function handleGetCancelVDriveOrderFee() {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await getCancelVDriveOrderFee(id);
      setCancelVDriveOrderFee(data.data.cancellationFee);
      setIsDriverAssigned(data.data.isDriverAssigned);
      setCancelVDriveModal(true);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function getDrivers() {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await getVDriveOffers(id);
      if (data?.data?.length > 0) {
        setAssignDriverModal(data.data);
      } else {
        setNoDriverModal(true);
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }
  const { invalidateByPrefix } = useInvalidateQueries();

  async function markAsCompleted() {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await markOrderAsCompleted(id);
      setData(data.data);
      setMarkOrderAsCompletedModal(false);
      SuccessToast(data.message);
      invalidateByPrefix('orders');
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      getOrderDetails();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Do not call closeDetailsModal() on unmount. When the user uses the browser
  // back button, we unmount and the cleanup would call navigate(-1), which
  // would go back again and skip the previous page (e.g. delivery details).
  // closeDetailsModal is still used for explicit close/back actions in the UI.

  const [historyAssignModal, setHistoryAssignModal] = useState<
    VDriveGetQuoteHistoryResponse[] | null
  >(null);

  const handleHistoryAssign = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await getOrdersAssignHistory(id);
      setHistoryAssignModal(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({
    Icon,
    title,
    mb = '5',
  }: {
    Icon: React.ElementType;
    title: string;
    mb?: string;
  }) => {
    return (
      <Div $flex $gap="16px" $alignCenter $mb={mb}>
        <Icon />
        <H5 $m="0">{title}</H5>
      </Div>
    );
  };

  const SectionInfo = ({
    info,
    info2,
    amount,
    hasAmount,
    item,
    itemChild,
    grayAmount,
    instructions,
    infoComponent,
  }: {
    info?: string | null | undefined;
    info2?: string | null | undefined;
    amount?: number;
    hasAmount?: boolean;
    item?: boolean;
    itemChild?: boolean;
    grayAmount?: boolean;
    instructions?: string | null | undefined;
    infoComponent?: React.ReactNode;
  }) => {
    const formattedAmount = formatter.format(
      amount ? centsToDollars(amount) : 0
    );

    return (
      <>
        <Div $p="0 0 0 48px" $flex $gap="16px" $between $alignCenter>
          {infoComponent && infoComponent}
          {info && (
            <P $bold={item} $semiBold={itemChild} $m="0">
              {info || '/'} {info2 && <span>{info2}</span>}
            </P>
          )}
          {hasAmount &&
            (grayAmount ? (
              <P $color={lightTheme.darkGray} $m="0">
                {formattedAmount}
              </P>
            ) : (
              <P $bold $color={lightTheme.primary} $m="0">
                {formattedAmount}
              </P>
            ))}
        </Div>
        {instructions && (
          <Disclaimer $color={lightTheme.lightGray} $m="0" $ml="48" $mb="4">
            {instructions}
          </Disclaimer>
        )}
      </>
    );
  };

  const closeDetailsModal = () => {
    navigate(-1);
    setData(null);
  };

  const hasNotCourierInfo =
    data?.delivery?.courier?.name === null &&
    data?.delivery?.courier?.name === null &&
    data?.delivery?.courier?.name === null;

  const customerAddress = data?.formattedDeliveryAddress;
  const isManualOrder = data?.creationType === 'manual';
  const isNewOrder = data?.status === 'new';
  const isPos = data?.posPartnerId !== null && data?.posPartnerId !== undefined;

  const Icons = () => (
    <Div $flex $alignCenter $gap="4px">
      {data?.posPartnerId !== null && data?.posPartnerId !== undefined && (
        <TooltipWrapper
          tooltip={
            data?.posInjectionStatus === PosInjectionStatus.INJECTED
              ? 'Successfully pushed to POS'
              : data?.posInjectionStatus === PosInjectionStatus.REJECTED
              ? 'Failed to push to POS'
              : undefined
          }
        >
          <Icon>
            {data?.posInjectionStatus === PosInjectionStatus.INJECTED ? (
              <PosSuccessIcon />
            ) : data?.posInjectionStatus === PosInjectionStatus.REJECTED ? (
              <PosFailedIcon />
            ) : (
              <PosIcon />
            )}
          </Icon>
        </TooltipWrapper>
      )}

      {isAdmin && data?.source && (
        <ProviderConnectionTypeComponent
          connectionType={data?.source}
          shortText
        />
      )}

      {isManualOrder && <ManualIconTooltip tooltip="Manual order" />}

      {data?.timestamps?.scheduledFor && (
        <ScheduledIconTooltip
          scheduledFor={data?.timestamps?.scheduledFor ?? ''}
          timeZone={data?.timeZone ?? ''}
        />
      )}
    </Div>
  );

  const courierPhoneNumber =
    data?.vDrive?.driverPhoneNumberForMerchant ??
    data?.vDrive?.driverPhone ??
    data?.delivery?.courier?.phoneNumber;

  const courierName = data?.vDrive?.driverName ?? data?.delivery?.courier?.name;

  return (
    <>
      <AdminHeader title="Order Details">
        {isAdmin && (
          <Div $flex $alignCenter $gap="16px">
            {data?.delivery?.type === DeliveryType.restaurant &&
              data?.vDriveEnabled &&
              data?.vDrive && (
                <OrdersDropdown handleHistoryAssign={handleHistoryAssign} />
              )}
          </Div>
        )}
      </AdminHeader>
      {loading && <AdminSpinner />}

      {!!data && (
        <Div $box $p="24px" $pSM="20px" $m="32px 0" $mSM="20px 0">
          <Div $flex>
            <Div $flex $center $column $flexNone $gapSM="8px">
              <Img
                alt=""
                src={providerLogo(data.provider, true)}
                $width="120px"
                $widthSM="100px"
              />
              {(!isPos || !data.posType) && <Icons />}
              {isPos && data.posType && (
                <Div
                  $relative
                  $border="1px solid #e3e3e3"
                  $borderRadius="12px"
                  $p="0 12px"
                  $pSM="0 8px"
                  $mbSM="0"
                  $mt="8"
                >
                  <Div
                    $absolute
                    $top="-12px"
                    $left="50%"
                    $transform="translateX(-50%)"
                  >
                    <Icons />
                  </Div>

                  <Img
                    alt=""
                    src={renderPosImage(data.posType)}
                    $width="80px"
                    $widthSM="80px"
                    $verticalAlign="middle"
                    $m="-4px auto"
                    $mSM="0"
                  />
                </Div>
              )}
            </Div>
            <VerticalSeparator $m="0 24px" $mSM="0 16px" />
            <Div $flex $between $columnSM $gap="16px" $width="100%">
              <Div>
                {(isAdmin || isPartner) && (
                  <>
                    {!data.brand.isLocation && (
                      <UnderlineHoverLink to={`/brand/${data.brand.id}`}>
                        <H3 $mb="8">{data.brand?.name}</H3>
                      </UnderlineHoverLink>
                    )}
                    <UnderlineHoverLink to={`/locations/${data.location.id}`}>
                      <H5 $mb="8">{data.location?.name}</H5>
                    </UnderlineHoverLink>
                  </>
                )}
                {isBrandOwner && (
                  <>
                    {!data.brand.isLocation && (
                      <H3 $mb="8">{data.brand?.name}</H3>
                    )}
                    <H5 $mb="8">{data.location?.name}</H5>
                  </>
                )}
                <P $m="0">
                  Order ID: <span>{data.id}</span>
                </P>
                <P $m="0">Number: {data.number}</P>

                <Div $flex $alignCenter $gap="4px">
                  <P $m="0">Status: </P>
                  <OrdersStatusPill
                    status={data?.status}
                    rejectReason={data?.rejectReason ?? ''}
                  />{' '}
                </Div>
              </Div>

              <Div $flex $column $alignEnd $desktop>
                <Div $flex $justifyEnd $gap="16px" $wrap $alignCenter>
                  <Div
                    $flex
                    $justifyEnd
                    $gap="16px"
                    $alignCenter
                    onClick={() => isAdmin && setModalIsOpen((prev) => !prev)}
                    $pointer={isAdmin}
                  >
                    <RecoveryStatusBadge recovery={data?.recovery} />
                    {isAdmin && (
                      <Icon
                        $shadow
                        $background={lightTheme.white}
                        $backgroundSize="40"
                        $pointer
                        $size="28"
                        $sizeSM="28"
                      >
                        <RecoveryIcon />
                      </Icon>
                    )}
                  </Div>
                  {isManualOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => setDeleteManualOrderModal(data)}
                      $size="24"
                    >
                      <DeleteIconRed />
                    </Icon>
                  )}

                  {isManualOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => {
                        setOrderToEdit(data);
                        closeDetailsModal();
                        navigate(ADMIN_EDIT_MANUAL_ORDER);
                      }}
                      $size="28"
                    >
                      <EditPenIconRed />
                    </Icon>
                  )}
                  {!isManualOrder && !isNewOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => setPrintOrderModal(data)}
                      $size="24"
                    >
                      <PrinterIcon />
                    </Icon>
                  )}

                  {isAdmin &&
                    !isManualOrder &&
                    !isPos &&
                    (data?.status === OrderStatus.accepted ||
                      data?.status === OrderStatus.completed) && (
                      <TooltipWrapper
                        tooltip={
                          data?.status === OrderStatus.completed
                            ? `Order is already completed.`
                            : ''
                        }
                        $maxWidth="40px"
                      >
                        <Icon
                          $shadow
                          $background={lightTheme.white}
                          $backgroundSize="40"
                          $pointer={data?.status === OrderStatus.accepted}
                          onClick={() =>
                            data?.status === OrderStatus.accepted
                              ? setMarkOrderAsCompletedModal(true)
                              : null
                          }
                          $size="28"
                        >
                          <CheckRedIcon
                            $color={
                              data?.status === OrderStatus.completed
                                ? lightTheme.green
                                : lightTheme.primary
                            }
                          />
                        </Icon>
                      </TooltipWrapper>
                    )}
                </Div>
              </Div>

              <Div $mobile>
                {data?.recovery !== null && data?.recovery !== undefined && (
                  <Div $mb="16">
                    <RecoveryStatusBadge recovery={data?.recovery} />
                  </Div>
                )}
                <Div $flex $alignCenter $gap="16px" $gapSM="8px" $wrap>
                  {isAdmin && (
                    <Icon
                      $sizeSM="28"
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                    >
                      <RecoveryIcon />
                    </Icon>
                  )}
                  {isManualOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => setDeleteManualOrderModal(data)}
                      $size="24"
                    >
                      <DeleteIconRed />
                    </Icon>
                  )}

                  {isManualOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => {
                        setOrderToEdit(data);
                        closeDetailsModal();
                        navigate(ADMIN_EDIT_MANUAL_ORDER);
                      }}
                      $size="28"
                    >
                      <EditPenIconRed />
                    </Icon>
                  )}
                  {!isManualOrder && !isNewOrder && (
                    <Icon
                      $shadow
                      $background={lightTheme.white}
                      $backgroundSize="40"
                      $pointer
                      onClick={() => setPrintOrderModal(data)}
                      $size="24"
                    >
                      <PrinterIcon />
                    </Icon>
                  )}

                  {isAdmin &&
                    !isManualOrder &&
                    !isPos &&
                    (data?.status === OrderStatus.accepted ||
                      data?.status === OrderStatus.completed) && (
                      <Icon
                        $shadow
                        $background={lightTheme.white}
                        $backgroundSize="40"
                        $pointer={data?.status === OrderStatus.accepted}
                        onClick={() =>
                          data?.status === OrderStatus.accepted
                            ? setMarkOrderAsCompletedModal(true)
                            : null
                        }
                        $size="28"
                      >
                        <CheckRedIcon
                          $color={
                            data?.status === OrderStatus.completed
                              ? lightTheme.green
                              : lightTheme.primary
                          }
                        />
                      </Icon>
                    )}
                </Div>
                {isManualOrder && (
                  <P $bold $m="0" $mt="16" $right>
                    <span>Manual Order</span>
                  </P>
                )}
              </Div>
            </Div>
          </Div>

          <Div $flex $mt="32" $gap="32px" $columnSM $gapSM="0">
            <Div $width="50%" $widthSM="100%">
              <Div $borderTop $borderBottom $p="16px 0">
                <SectionTitle Icon={OrderInfoIcon} title="Order Info" />
                <Div $p="0 0 0 48px">
                  <TextWithTooltipIcon
                    text={DateTime.fromISO(data.date)
                      .setZone(data.timeZone)
                      .toFormat('MMM dd, yyyy, hh:mm a')}
                    tooltip={`Timezone: ${data?.timeZone}`}
                  />
                  <br />
                  {data?.timestamps?.scheduledFor && (
                    <TextWithTooltipIcon
                      text={`Scheduled for: ${DateTime.fromISO(
                        data?.timestamps?.scheduledFor || ''
                      )
                        .setZone(data.timeZone)
                        .toFormat('MMM dd, hh:mm a')}`}
                      tooltip={`Timezone: ${data?.timeZone}`}
                    />
                  )}
                </Div>

                {data.provider !== ('unknown' as unknown as Provider) && (
                  <SectionInfo info={providerName(data.provider)} />
                )}
              </Div>
              <Div $borderBottom $p="16px 0">
                <Div>
                  <SectionTitle
                    Icon={OrderCustomerIcon}
                    title="Customer Details"
                  />
                </Div>
                <SectionInfo info={data?.customer?.name} />
                {data?.customer?.phoneNumber && (
                  <SectionInfo
                    infoComponent={
                      <PhoneNumberText phone={data?.customer?.phoneNumber} />
                    }
                  />
                )}

                {data?.customer?.phoneCode && (
                  <SectionInfo
                    info={`Pin: ${data?.customer?.phoneCode || '/'}`}
                  />
                )}
                {data?.vDrive?.specialOfferPhone && (
                  <SectionInfo
                    info={`Opted in number: ${
                      data?.vDrive?.specialOfferPhone?.isUSNumber
                        ? data?.vDrive?.specialOfferPhone?.nationalNumber
                        : data?.vDrive?.specialOfferPhone?.number
                    }`}
                  />
                )}
                {data?.customer?.email && (
                  <SectionInfo info={`Email: ${data?.customer?.email}`} />
                )}
                <Div
                  $flex
                  $alignCenter
                  $columnSM
                  $alignStartSM
                  $gap="16px"
                  $gapSM="8px"
                >
                  {data?.delivery?.address?.address && (
                    <SectionInfo info={customerAddress} />
                  )}
                  {data?.vDrive?.distanceMiles !== null &&
                  data?.vDrive?.distanceMiles &&
                  data?.vDrive?.distanceMiles > 0 ? (
                    <Div $mlSM="48">
                      <DistancePill
                        miles={data.vDrive.distanceMiles}
                        dspMiles={data.vDrive.dspDistanceMiles}
                      />
                    </Div>
                  ) : null}
                </Div>
              </Div>

              {data.type === 'pickup' && !isManualOrder && (
                <Div $borderBottom $p="16px 0">
                  <SectionTitle
                    Icon={OrderCourierIcon}
                    title="Pickup Details"
                  />

                  <SectionInfo
                    info={`Pickup: ${DateTime.fromISO(
                      data.timestamps?.pickupAt || ''
                    )
                      .setZone(data.timeZone)
                      .toFormat('MMM dd, hh:mm a')}`}
                  />
                </Div>
              )}

              {data.type === 'delivery' && !isManualOrder && (
                <Div $borderBottom>
                  <Div
                    $p="16px 0"
                    $flex
                    $between
                    $alignCenter
                    $columnSM
                    $gap="20px"
                  >
                    <Div $widthSM="100%">
                      {data?.delivery?.type !== DeliveryType.restaurant &&
                        (!hasNotCourierInfo ? (
                          <Div
                            $flex
                            $between
                            onClick={() => setCourierModalIsOpen(true)}
                            $pointer
                          >
                            <SectionTitle
                              Icon={OrderCourierIcon}
                              title="Courier Details"
                            />
                            <ArrowRed />
                          </Div>
                        ) : (
                          <SectionTitle
                            Icon={OrderCourierIcon}
                            title="Courier Details"
                          />
                        ))}

                      {data?.delivery?.type === DeliveryType.restaurant && (
                        <SectionTitle
                          Icon={OrderCourierIcon}
                          title="Driver Details"
                        />
                      )}

                      {data?.delivery?.type !== DeliveryType.restaurant && (
                        <>
                          {courierName && <SectionInfo info={courierName} />}

                          {courierPhoneNumber && (
                            <SectionInfo info={courierPhoneNumber} />
                          )}

                          {data?.vDrive?.driverPinCodeForMerchant && (
                            <SectionInfo
                              info={`Pin: ${data?.vDrive?.driverPinCodeForMerchant}`}
                            />
                          )}
                        </>
                      )}

                      <SectionInfo
                        info={`Pickup: ${DateTime.fromISO(
                          data?.timestamps?.pickupAt || ''
                        )
                          .setZone(data.timeZone)
                          .toFormat('MMM dd, hh:mm a')}`}
                      />

                      {data?.delivery?.type !== DeliveryType.restaurant && (
                        <SectionInfo
                          info={`Delivery: ${providerName(
                            data.deliveryProvider ?? data.provider,
                            { withoutPos: true }
                          )}`}
                        />
                      )}

                      {data?.delivery?.type === DeliveryType.restaurant &&
                        data?.vDriveProvider === null && (
                          <SectionInfo
                            info={`Delivery: `}
                            info2={`Self Delivery`}
                          />
                        )}

                      {data?.delivery?.type === DeliveryType.restaurant &&
                        data?.vDriveProvider !== null && (
                          <SectionInfo info={`Delivery: vDrive`} />
                        )}
                      {data?.delivery?.notes !== null &&
                        data?.delivery?.notes !== undefined && (
                          <SectionInfo
                            info={`Notes: ${data?.delivery?.notes}`}
                          />
                        )}
                    </Div>
                    <Div $widthSM="100%">
                      {isAdmin &&
                        data?.delivery?.type === DeliveryType.restaurant &&
                        data?.vDriveEnabled &&
                        data?.vDriveProvider !== null && (
                          <PoweredByComponent data={data} />
                        )}
                    </Div>
                  </Div>
                  {(isAdmin || isPartner) &&
                    data?.delivery?.type === DeliveryType.restaurant &&
                    data?.vDriveEnabled &&
                    data?.vDrive && (
                      <VDriveStatusComponent
                        vDrive={data?.vDrive}
                        brand={data?.brand}
                        id={data?.id}
                        getDrivers={getDrivers}
                        handleGetCancelVDriveOrderFee={
                          handleGetCancelVDriveOrderFee
                        }
                        setChangeAddressModal={setChangeAddressModal}
                      />
                    )}
                </Div>
              )}

              {data?.notes && (
                <Div $borderBottom $p="16px 0">
                  <SectionTitle Icon={OrderNotesIcon} title="Customer Notes" />
                  <SectionInfo info={data?.notes} />
                </Div>
              )}
              {isAdmin && interceptorCallData?.recordingUrl && (
                <Div $flex $column $p="12px 0 0 0" $pSM="12px 0 12px 0">
                  <Div
                    $pointer
                    $flex
                    $alignCenter
                    $between
                    onClick={() => setInterceptorCallCollapse((prev) => !prev)}
                  >
                    <SectionTitle
                      Icon={GrayCircleEarphoneIcon}
                      title="AI Agent Outbound Call Recording"
                      mb="0"
                    />
                    <Icon $rotate={interceptorCallCollapse ? '180deg' : '0'}>
                      <ChevronSmall />
                    </Icon>
                  </Div>
                  <CollapsibleComponent
                    expanded={interceptorCallCollapse}
                    elementHeight={elementHeightInterceptorCall}
                    marginTop="16px"
                  >
                    <Div ref={refInterceptorCall} $flex $column $gap="12px">
                      <CallRecordingAndTranscription
                        interceptorCallData={interceptorCallData}
                        customerData={data?.customer as CustomerInfo}
                      />
                    </Div>
                  </CollapsibleComponent>
                </Div>
              )}
            </Div>
            <Div $width="50%" $widthSM="100%">
              <Div $borderTop $borderBottom $p="16px 0">
                <Div $flex $alignCenter $between>
                  <SectionTitle
                    Icon={OrderUtilsIcon}
                    title="Bring Utensils & Napkins"
                  />
                  <P $m="0">{data?.addDisposableItems ? 'Yes' : 'No'}</P>
                </Div>
              </Div>

              {!isManualOrder && (
                <>
                  {' '}
                  <Div $p="16px 0 0 0">
                    <SectionTitle
                      Icon={OrderDetailsIcon}
                      title="Order Details"
                    />
                    <Div $mb="24">
                      {data?.items?.map(
                        (
                          { price, name, options, quantity, instructions },
                          i
                        ) => {
                          return (
                            <div key={i}>
                              <SectionInfo
                                key={i}
                                item
                                info={`${name} (x${quantity})`}
                                amount={price}
                                hasAmount
                                instructions={instructions}
                              />
                              <Div $ml="20">
                                {options?.map(
                                  (
                                    {
                                      modifierName,
                                      name,
                                      quantity,
                                      price,
                                      children,
                                    },
                                    i
                                  ) => {
                                    return (
                                      <>
                                        <SectionInfo
                                          key={i}
                                          info={`${
                                            modifierName
                                              ? `${modifierName}: `
                                              : ''
                                          }${name} (x${quantity})`}
                                          amount={price}
                                          hasAmount
                                          itemChild
                                          grayAmount
                                        />
                                        {children?.map(
                                          ({ name, quantity, price }, i) => {
                                            return (
                                              <SectionInfo
                                                key={i}
                                                info={`+ ${
                                                  modifierName
                                                    ? `${modifierName}: `
                                                    : ''
                                                }${name} (x${quantity})`}
                                                amount={price}
                                                hasAmount
                                                grayAmount
                                              />
                                            );
                                          }
                                        )}
                                      </>
                                    );
                                  }
                                )}
                              </Div>
                            </div>
                          );
                        }
                      )}
                    </Div>

                    <SectionInfo
                      info={'Subtotal'}
                      amount={data?.charges?.subTotal || 0}
                      hasAmount
                    />
                    <SectionInfo
                      info={'Tax'}
                      amount={data?.charges?.tax || 0}
                      hasAmount
                    />
                    {isAdmin && (
                      <SectionInfo
                        info={'Tips'}
                        amount={data?.charges?.tips || 0}
                        hasAmount
                      />
                    )}
                    {!isAdmin &&
                    (data.brand.type !== BrandType.vGrubs ||
                      data?.brand.isLocation) ? (
                      <SectionInfo
                        info={'Tips'}
                        amount={data?.charges?.tips || 0}
                        hasAmount
                      />
                    ) : null}
                    <SectionInfo
                      info={'Discount'}
                      amount={data?.charges?.discount || 0}
                      hasAmount
                    />
                    {isAdmin && (
                      <SectionInfo
                        info={'Delivery Fee'}
                        amount={data?.charges?.deliveryFee || 0}
                        hasAmount
                      />
                    )}
                    {!isAdmin &&
                    (data.brand.type !== BrandType.vGrubs ||
                      data?.brand.isLocation) ? (
                      <SectionInfo
                        info={'Delivery Fee'}
                        amount={data?.charges?.deliveryFee || 0}
                        hasAmount
                      />
                    ) : null}
                    <SectionInfo
                      info={'Service Fee'}
                      amount={data?.charges?.serviceFee || 0}
                      hasAmount
                    />
                  </Div>
                </>
              )}
            </Div>
          </Div>
        </Div>
      )}

      <RecoveryModal
        modalIsOpen={modalIsOpen}
        closeModal={() => setModalIsOpen(false)}
        recovery={data?.recovery}
        setData={setData}
        id={Number(id)}
      />
      <CourierModal
        modalIsOpen={courierModalIsOpen}
        closeModal={() => setCourierModalIsOpen(false)}
        courier={data?.delivery?.courier}
      />
      <DeleteOrderModal closeModal={closeDetailsModal} />

      {data && (
        <VDriveAssignDriver
          order={data}
          setOrder={setData}
          modalIsOpen={assignDriverModal}
          closeModal={() => {
            setAssignDriverModal(null);
          }}
          noDriverModal={noDriverModal}
          setNoDriverModal={setNoDriverModal}
          setLoading={setLoading}
        />
      )}
      <PrintReceiptModal />
      <WhatIsThisModal
        modalIsOpen={whatIsThisModal}
        closeModal={() => setWhatIsThisModal(false)}
      />

      <ChangeAddress
        modalIsOpen={changeAddressModal}
        closeModal={() => setChangeAddressModal(null)}
        setLoading={setLoading}
        setData={setData}
      />

      <CancelVDriveModal
        cancelVDriveModal={cancelVDriveModal}
        closeModal={() => setCancelVDriveModal(false)}
        setLoading={setLoading}
        setData={setData}
        fee={cancelVDriveOrderFee}
        isDriverAssigned={isDriverAssigned}
        id={data?.id?.toString() || ''}
      />

      <MarkOrderAsCompletedModal
        modalIsOpen={markOrderAsCompletedModal}
        closeModal={() => setMarkOrderAsCompletedModal(false)}
        handleMarkAsCompleted={markAsCompleted}
      />

      <HistoryAssign
        closeModal={() => setHistoryAssignModal(null)}
        historyAssignModal={historyAssignModal}
        chosenVDriveProvider={data?.vDrive?.provider ?? null}
        chosenVDriveSource={data?.vDrive?.source ?? null}
      />
    </>
  );
};
